import { useState } from 'react';
import { supabase } from '../supabase';
import { UNTITLED_DOCUMENT_TITLE } from '../editor/defaultTitles';

export interface UseCreateDocument {
  creating: boolean;
  error: string | null;
  /**
   * Create an untitled document in the org, link it to the project, and return
   * the new document id (or null on failure, with `error` set).
   */
  create: (orgId: string, projectId: string) => Promise<string | null>;
}

/**
 * "Create new document" from the Project page. Mirrors the Documents list
 * `+ New document` flow (RPC `create_document` defaulting to the org's first
 * document type) and then links the new doc to the project via the
 * `project_documents` join — the same insert as `useProjectMembership.linkDocument`.
 */
export function useCreateDocument(): UseCreateDocument {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (
    orgId: string,
    projectId: string
  ): Promise<string | null> => {
    setCreating(true);
    setError(null);
    try {
      // Default to the org's first document type (alphabetical), as the
      // Documents list does — `create_document`'s `p_type_id` is optional.
      const { data: types } = await supabase
        .from('document_types')
        .select('id')
        .eq('org_id', orgId)
        .is('archived_at', null)
        .order('name', { ascending: true })
        .limit(1);
      const typeId = (types?.[0]?.id as string | undefined) ?? undefined;

      const { data, error: rpcErr } = await supabase.rpc('create_document', {
        p_org_id: orgId,
        p_title: UNTITLED_DOCUMENT_TITLE,
        p_type_id: typeId
      });
      const env = data as { ok?: boolean; document?: { id?: string } } | null;
      if (rpcErr || !env?.ok || !env.document?.id) {
        setError(
          rpcErr?.message ?? "Couldn't create the document. Please try again."
        );
        return null;
      }
      const newId = env.document.id;

      const { error: linkErr } = await supabase
        .from('project_documents')
        .insert({ org_id: orgId, project_id: projectId, document_id: newId });
      if (linkErr) {
        setError(linkErr.message);
        return null;
      }
      return newId;
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unexpected error creating document'
      );
      return null;
    } finally {
      setCreating(false);
    }
  };

  return { creating, error, create };
}
